package com.upcominds.expedite.features.challenges

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.upcominds.expedite.data.challenges.ChallengesRepository
import com.upcominds.expedite.data.mobilelogs.logMobileError
import com.upcominds.expedite.data.user.UserRepository
import com.upcominds.expedite.model.Challenge
import com.upcominds.expedite.dto.CommunityCompetitionDTO
import com.upcominds.expedite.dto.CommunityCompetitionLeaderboardDTO
import com.upcominds.expedite.dto.CompetitionRepresentationDTO
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch

//challengesUiState
data class ChallengesUiState(
    val loading: Boolean = false,
    val error: String? = null,
    val myChallenges: List<Challenge> = emptyList(),
    val popularChallenges: List<Challenge> = emptyList(),
    val competitions: List<CommunityCompetitionDTO> = emptyList(),
    val selectedCompetition: CommunityCompetitionDTO? = null,
    val selectedLeaderboard: CommunityCompetitionLeaderboardDTO? = null,
    val selectedRepresentation: CompetitionRepresentationDTO? = null
)

class ChallengesViewModel(
    private val userRepository: UserRepository,
    private val challengesRepository: ChallengesRepository
): ViewModel() {

    private var challengesLoadJob: Job? = null

    private val _uiState = MutableStateFlow(ChallengesUiState())
    val uiState: StateFlow<ChallengesUiState> = _uiState.asStateFlow()

    init {
        //loadChallenges()
    }

    fun loadChallenges() {
        challengesLoadJob?.cancel()
        challengesLoadJob = viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                userRepository.getCurrentUser()
                val participated = challengesRepository.getChallengeParticipated()
                val available = challengesRepository.getChallenges()
                val competitions = challengesRepository.getCommunityCompetitions()
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = null,
                    myChallenges = participated.filterNot { it.isCommunityCompetition },
                    popularChallenges = available.filterNot { it.isCommunityCompetition },
                    competitions = competitions,
                )
            } catch (e: Exception) {
                logMobileError("ChallengesViewModel.loadChallenges", e)
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = e.message
                )
            }
        }
    }

    fun loadCompetition(id: String, activeCommunityId: String? = null) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val competition = challengesRepository.getCommunityCompetition(id)
                val currentRepresentation = challengesRepository.getCompetitionRepresentation(id)
                val representation = if (
                    activeCommunityId != null &&
                    !currentRepresentation.locked &&
                    currentRepresentation.options.any { it.id == activeCommunityId } &&
                    (!currentRepresentation.confirmed || currentRepresentation.selectedCommunityId != activeCommunityId)
                ) {
                    challengesRepository.selectCompetitionRepresentation(id, activeCommunityId)
                } else {
                    currentRepresentation
                }
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    selectedCompetition = competition,
                    selectedRepresentation = representation,
                    competitions = _uiState.value.competitions.map {
                        if (it.id == competition.id) competition else it
                    }
                )
            } catch (e: Exception) {
                logMobileError("ChallengesViewModel.loadCompetition", e)
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun selectCompetitionRepresentation(competitionId: String, communityId: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val representation = challengesRepository
                    .selectCompetitionRepresentation(competitionId, communityId)
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    selectedRepresentation = representation
                )
            } catch (e: Exception) {
                logMobileError("ChallengesViewModel.selectCompetitionRepresentation", e)
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }

    fun loadCompetitionLeaderboard(id: String) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                val leaderboard = challengesRepository.getCommunityCompetitionLeaderboard(id)
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    selectedLeaderboard = leaderboard
                )
            } catch (e: Exception) {
                logMobileError("ChallengesViewModel.loadCompetitionLeaderboard", e)
                _uiState.value = _uiState.value.copy(loading = false, error = e.message)
            }
        }
    }
}
